import flatpickr from 'flatpickr';
import template from './calendar.html';
import '../calendar.scss';

/**
 * @ngdoc controller
 * @name OdsCalendar
 * @description
 * This component renders a input field and mount an instance of the plugin Flatpickr.
 */
class OdsCalendar {
  constructor($element) {
    'ngInject';

    /**
     * The local reference for the Flatpickr Object.
     * @type {Flatpickr}
     */
    this.flatpickr = flatpickr;
    /**
     * The local reference for the component's input field, required by Flatpickr.
     * @type {Object}
     */
    [this.input] = $element[0].getElementsByTagName('input');
    /**
     * The local reference for the component's field value, it will be used to set the selectedDate.
     * @type {String}
     */
    this.defaultLabel = 'MM/DD/YYYY to MM/DD/YYYY';
  }
  /**
   * We're going to extend the Flatpickr's options with the callback action passed
   * through component's param and initialize the Flatpickr plugin.
   */
  $onInit() {
    const flatpickrOptions = Object.assign(
      {
        onChange: this.handleOnDateChange.bind(this),
        onClose: this.handleOnClose.bind(this),
        onReady: this.handleOnInstanceReady.bind(this),
      },
      this.options,
    );
    this.inputLabel = this.inputLabel || this.defaultLabel;
    this.flatpickr(this.input, flatpickrOptions);
  }
  /**
   * Callback method for the Flatpickr's `onClose` event.
   *
   * @param {Date}      selectedDates  the date Object provided by the plugin.
   * @param {String}    dateStr        the date string provided by the plugin.
   * @param {Flatpickr} instance       the flatpickr's intance returned by the plugin.
   *
   * @see https://flatpickr.js.org/events/
   */
  handleOnClose(selectedDates, dateStr, instance) {
    this.onClose({ selectedDates, dateStr, instance });
  }
  /**
   * Callback method for the Flatpickr's `onChange` event.
   *
   * @param {Date}      selectedDates  the date Object provided by the plugin.
   * @param {String}    dateStr        the date string provided by the plugin.
   * @param {Flatpickr} instance       the flatpickr's intance returned by the plugin.
   *
   * @see https://flatpickr.js.org/events/
   */
  handleOnDateChange(selectedDates, dateStr, instance) {
    this.onDateChange({ selectedDates, dateStr, instance });
  }
  /**
   * Sent the same Flatpickr's params back to the implementation when the plugin is ready to be used.
   *
   * @param {Date}      selectedDates  the date Object provided by the plugin.
   * @param {String}    dateStr        the date string provided by the plugin.
   * @param {Flatpickr} instance       the flatpickr's intance returned by the plugin.
   *
   * @see https://flatpickr.js.org/events/
   */
  handleOnInstanceReady(selectedDates, dateStr, instance) {
    this.onInstanceReady({
      selectedDates,
      dateStr,
      instance,
    });
  }
}
/**
 * @ngdoc component
 * @name OdsCalendar
 * @description
 * The calendar component.
 */
export default {
  /**
   * The controller class for the component.
   * @type {OdsCalendar}
   */
  controller: OdsCalendar,
  /**
   * The HTML template for the component.
   * @type {String}
   */
  template,
  /**
   * Component bindings
   * @type {Object}
   * @property {String}           inputLabel       the input's placeholder value.
   * @external {FlatpickrOptions} options          @see https://flatpickr.js.org/options/
   * @property {Function}         onDateChange     the callback fired every time the Flatpickr's
   *                                               event `onChange` is being fired.
   * @property {Function}         onClose          the callback fired every time the Flatpickr's
   *                                               event `onClose` is being fired.
   * @property {Function}         onInstanceReady  callback fired every time the Flatpickr's event
   *                                               `onReady` is being fired.

   */
  bindings: {
    inputLabel: '@',
    options: '<',
    onDateChange: '&',
    onClose: '&',
    onInstanceReady: '&',
  },
};
